import { memo } from 'react';

import styles from './OrbitBackdrop.module.css';

/**
 * The atmospheric scene behind sign-in.
 *
 * Deliberately no figure at the centre: before you have signed in, the app does
 * not know whose aspects these would be. Every ring shares the same 0.3412 tilt
 * as the real orbit view, so the two read as one space.
 *
 * Only translation, scale and stroke-dashoffset animate. A rigid rotation
 * preserves a circle but shears an ellipse off its own path — see CLAUDE.md.
 */
export const OrbitBackdrop = memo(function OrbitBackdrop() {
  return (
    <svg
      className={styles.scene}
      viewBox="0 0 852 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id="signinGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
          <stop offset="45%" stopColor="var(--accent)" stopOpacity="0.10" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </radialGradient>
        <filter id="signinSoften" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="8" />
        </filter>
      </defs>

      <g fill="var(--text)">
        <circle cx="118" cy="142" r="1.2" opacity=".40" />
        <circle cx="286" cy="232" r="1" opacity=".28" />
        <circle cx="432" cy="118" r="1.4" opacity=".48" />
        <circle cx="642" cy="286" r="1" opacity=".26" />
        <circle cx="760" cy="182" r="1.3" opacity=".40" />
        <circle cx="196" cy="418" r="1" opacity=".24" />
        <circle cx="86" cy="712" r="1.4" opacity=".42" />
        <circle cx="700" cy="640" r="1" opacity=".30" />
        <circle cx="560" cy="806" r="1.2" opacity=".34" />
      </g>

      <ellipse cx="300" cy="620" rx="340" ry="118" fill="url(#signinGlow)" />

      {/* Orbits, drawn past the frame so the scene continues beyond it */}
      <g fill="none" stroke="var(--accent)" strokeWidth="1">
        <ellipse cx="300" cy="620" rx="230" ry="80" opacity=".22" />
        <ellipse cx="300" cy="620" rx="350" ry="122" opacity=".16" />
        <ellipse cx="300" cy="620" rx="470" ry="164" opacity=".10" />
      </g>

      {/* Each trail's dash cycle equals its own ellipse perimeter, so the
          travelling segment never snaps at the path origin. */}
      <g fill="none" stroke="var(--accent-brightest)" strokeWidth="1.6" strokeLinecap="round" opacity=".7">
        <ellipse className={styles.trailA} cx="300" cy="620" rx="230" ry="80" />
        <ellipse className={styles.trailB} cx="300" cy="620" rx="350" ry="122" />
      </g>

      {/* The hub, lying in the same plane rather than floating above it */}
      <g fill="none" stroke="var(--accent-bright)" strokeLinecap="round">
        <ellipse cx="300" cy="620" rx="56" ry="19.5" strokeWidth="1" opacity=".30" />
        <ellipse cx="300" cy="620" rx="90" ry="31.3" strokeWidth="1" opacity=".24" />
        <ellipse cx="300" cy="620" rx="124" ry="43.1" strokeWidth="1" opacity=".18" />
        <ellipse className={styles.hubA} cx="300" cy="620" rx="56" ry="19.5" strokeWidth="1.6" opacity=".8" />
        <ellipse className={styles.hubC} cx="300" cy="620" rx="124" ry="43.1" strokeWidth="1.6" opacity=".8" />
      </g>
      <g fill="none" stroke="var(--accent-bright)" strokeWidth="1.5">
        <ellipse className={styles.ping} cx="300" cy="620" rx="124" ry="43.1" />
        <ellipse className={`${styles.ping} ${styles.ping2}`} cx="300" cy="620" rx="124" ry="43.1" />
      </g>

      <g stroke="var(--accent-bright)" strokeLinecap="round" opacity=".38">
        <path d="M 300 490 L 300 750" strokeWidth="1" />
        <path d="M 293 512 h 14" strokeWidth="1.25" />
        <path d="M 293 728 h 14" strokeWidth="1.25" />
      </g>

      <circle cx="300" cy="620" r="4" fill="var(--accent-brightest)" />
      <circle cx="300" cy="620" r="13" fill="var(--accent-brightest)" filter="url(#signinSoften)" opacity=".45" />

      {/* Two aspects in orbit, each sitting exactly on its ellipse */}
      <ellipse cx="508.4" cy="653.8" rx="8" ry="2.8" fill="var(--accent-bright)" opacity=".30" />
      <path d="M 508.4 653.8 L 508.4 631.8" stroke="var(--accent-bright)" strokeWidth="1" opacity=".45" />
      <circle cx="508.4" cy="631.8" r="20" fill="var(--accent)" filter="url(#signinSoften)" opacity=".45" />
      <circle cx="508.4" cy="631.8" r="9" fill="var(--accent-bright)" />
      <ellipse cx="31.9" cy="698.4" rx="7" ry="2.4" fill="var(--warm-bright)" opacity=".30" />
      <path d="M 31.9 698.4 L 31.9 679.4" stroke="var(--warm-bright)" strokeWidth="1" opacity=".45" />
      <circle cx="31.9" cy="679.4" r="16" fill="var(--warm)" filter="url(#signinSoften)" opacity=".40" />
      <circle cx="31.9" cy="679.4" r="7" fill="var(--warm-bright)" />
    </svg>
  );
});
